<template>
  <component :is="this.$route.meta.layout || 'div'">
    <ElConfigProvider :locale="curLocale">
      <router-view />
    </ElConfigProvider>
  </component>
</template>

<script lang="ts">
import useItemsStore from '@/composables/store/useItemsStore';
import useLocationsStore from '@/composables/store/useLocationsStore';
import usePartnerStore from '@/composables/store/usePartnerStore';
import useRoomTypesStore from '@/composables/store/useRoomTypesStore';
import useFunctionSpacesStore from '@/composables/store/useFunctionSpacesStore';
import useColor from '@/composables/useColor';
import { useFavicon } from '@vueuse/core';
import { ElConfigProvider } from 'element-plus';
import '@stripe/stripe-js';

import enLocale from 'element-plus/es/locale/lang/en';
import nlLocale from 'element-plus/es/locale/lang/nl';
import deLocale from 'element-plus/es/locale/lang/de';
import frLocale from 'element-plus/es/locale/lang/fr';
import fiLocale from 'element-plus/es/locale/lang/fi';

import { computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

export default {
  components: { ElConfigProvider },
  setup() {
    const {
      fetchPartner,
      primaryColor,
      secondaryColor,
      partnerName,
      faviconFilename,
      fontName,
      useRoundedBoxes,
      gtmId,
    } = usePartnerStore();
    const { fetchLocations } = useLocationsStore();
    const { setColors } = useColor();
    fetchPartner();
    fetchLocations();

    watch(
      [primaryColor, secondaryColor, fontName, useRoundedBoxes],
      ([newPrimary, newSecondary, chosenFont, useRounded]) => {
        const docStyle = document.documentElement.style;
        docStyle.setProperty('--primary-font', chosenFont.toString());
        docStyle.setProperty(
          '--el-border-radius-base',
          useRounded ? '4px' : '0',
        );
        setColors(`#${newPrimary}`, `#${newSecondary}`);
      },
    );

    watch(
      () => faviconFilename.value,
      (newFavicon) => {
        useFavicon(newFavicon);
      },
    );

    watch(
      () => partnerName.value,
      (name) => {
        document.title = name;
      },
    );

    watch(
      () => gtmId.value,
      (gtmId) => {
        if (gtmId) {
          const gtmScript = document.createElement('script');
          gtmScript.innerHTML = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
})(window,document,'script','dataLayer','${gtmId}');`;
          gtmScript.dataset['cookieconsent'] = 'statistics';
          gtmScript.type = 'text/plain';
          document.head.append(gtmScript);

          const cookiebotEmbed = document.createElement('script');
          cookiebotEmbed.id = 'Cookiebot';
          cookiebotEmbed.src = 'https://consent.cookiebot.com/uc.js';
          cookiebotEmbed.type = 'text/javascript';
          cookiebotEmbed.dataset['cbid'] = import.meta.env.VITE_COOKIEBOT_CBID;
          cookiebotEmbed.dataset['blockingmode'] = 'auto';
          document.head.append(cookiebotEmbed);
        }
      },
    );
    const { locale } = useI18n();
    const locales: Record<string, any> = {
      'nl-NL': nlLocale,
      'en-US': enLocale,
      'de-DE': deLocale,
      'fr-FR': frLocale,
      'fi-FI': fiLocale,
    };
    const curLocale = computed(() => locales[locale.value]);

    // Load all relevant location items etc. as soon as a location is selected, rather than when the data is needed
    useItemsStore();
    useRoomTypesStore();
    useFunctionSpacesStore();

    return { curLocale };
  },
};
</script>
